import { Link } from "react-router-dom";
import { Seo } from "../components/Seo";
import { MessageCircle, Check, X } from "lucide-react";
import { PA_BASE, IMAGES, img, waLink, BROKERAGE_DISCLAIMER } from "../config";
import { KineticHero } from "../components/KineticHero";
import { Reveal, Overline } from "../components/motion";

const ARE = [
  "A private air charter brokerage",
  "Your single point of contact for sourcing aircraft",
  "Independent of any fleet or operator",
  "Accountable for the detail, end to end",
];

const ARE_NOT = [
  "An aircraft owner or operator",
  "An airline or air carrier",
  "An employer of flight crew",
  "In control of aircraft operations",
];

const PRINCIPLES = [
  {
    n: "01",
    title: "Transparent brokerage",
    body: "You always know who operates your flight. We present the operator, the aircraft and the terms — and you approve before anything is confirmed.",
  },
  {
    n: "02",
    title: "Operator standards",
    body: "We source only from duly licensed operators holding the required air operator certificates and insurance for the flights they perform.",
  },
  {
    n: "03",
    title: "No fabricated urgency",
    body: "No countdown timers, no 'only one left', no invented availability. If an option exists, we'll show it. If it doesn't, we'll say so.",
  },
  {
    n: "04",
    title: "Discretion by default",
    body: "Your itinerary, passengers and purpose remain private. Information moves only to those who need it to operate the flight.",
  },
];

export default function AboutPage() {
  return (
    <>
      <Seo
        title="About JS Private Aviation — A Private Charter Brokerage"
        description="JS Private Aviation is the private charter arm of JS Group Holdings — arranging flights through independent aircraft operators, with transparent brokerage principles."
      />
      <KineticHero
        testId="about-hero"
        fullHeight={false}
        overline="About JS Private Aviation"
        lines={["A brokerage,", "by design."]}
        sub="JS Private Aviation is the private charter arm of JS Group Holdings — arranging flights through independent aircraft operators, for clients who value clarity over noise."
        image={img(IMAGES.skySunset, 2000)}
      />

      <section className="bg-[#F9F9F6] py-24 sm:py-32">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <div className="grid grid-cols-1 gap-px bg-black/10 md:grid-cols-2">
            <Reveal className="bg-[#F9F9F6]">
              <div className="h-full p-10 sm:p-14" data-testid="about-we-are">
                <Overline>What we are</Overline>
                <ul className="mt-8 space-y-4">
                  {ARE.map((x) => (
                    <li key={x} className="flex items-baseline gap-3 text-[15px] text-black/80">
                      <Check size={15} className="relative top-0.5 shrink-0 text-black/60" />
                      {x}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.1} className="bg-[#F9F9F6]">
              <div className="h-full p-10 sm:p-14" data-testid="about-we-are-not">
                <Overline>What we are not</Overline>
                <ul className="mt-8 space-y-4">
                  {ARE_NOT.map((x) => (
                    <li key={x} className="flex items-baseline gap-3 text-[15px] text-black/60">
                      <X size={15} className="relative top-0.5 shrink-0 text-black/40" />
                      {x}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>

          <div className="mt-24">
            <Reveal>
              <Overline>Principles</Overline>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight">How we operate.</h2>
            </Reveal>
            <div className="mt-14 grid grid-cols-1 gap-px bg-black/10 sm:grid-cols-2 lg:grid-cols-4">
              {PRINCIPLES.map((p, i) => (
                <Reveal key={p.n} delay={0.05 * i} className="bg-[#F9F9F6]">
                  <div className="h-full p-8 sm:p-10" data-testid={`principle-${p.n}`}>
                    <span className="font-mono text-sm text-black/30">{p.n}</span>
                    <h3 className="mt-4 font-display text-xl tracking-tight">{p.title}</h3>
                    <p className="mt-3 text-[13px] leading-relaxed text-black/55">{p.body}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <Reveal className="mt-24">
            <p className="max-w-3xl border-t border-black/10 pt-8 text-[12px] leading-relaxed text-black/40">
              {BROKERAGE_DISCLAIMER} Operational control of every flight rests with the operating carrier.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#0A0A0A] py-24 text-white">
        <div className="mx-auto flex max-w-[1400px] flex-wrap items-center justify-between gap-8 px-5 sm:px-8">
          <Reveal>
            <h2 className="max-w-xl font-display text-3xl sm:text-4xl tracking-tight leading-tight">
              Arrange your first charter with us.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex flex-wrap gap-4">
              <Link
                to={`${PA_BASE}/charter#request`}
                data-testid="about-quote-cta"
                className="inline-flex items-center gap-3 bg-white px-8 py-4 text-[12px] uppercase tracking-[0.22em] text-black transition-colors duration-300 hover:bg-white/85"
              >
                Request a Quote
              </Link>
              <a
                href={waLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="about-whatsapp-cta"
                className="inline-flex items-center gap-2 border border-white/35 px-8 py-4 text-[12px] uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:bg-white hover:text-black"
              >
                <MessageCircle size={15} /> WhatsApp
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
